import axios, { AxiosError } from 'axios'
import { defaultRequestInterceptors, defaultResponseInterceptors } from './config'

import { AxiosInstance, InternalAxiosRequestConfig, RequestConfig, AxiosResponse } from './types'
import { ElMessage } from 'element-plus'
import { REQUEST_TIMEOUT } from '@/constants'
import * as Sentry from '@sentry/vue'
import { useUserStoreWithOut } from '@/store/modules/user'

export const PATH_URL = import.meta.env.VITE_API_BASE_PATH

const abortControllerMap: Map<string, AbortController> = new Map()

const axiosInstance: AxiosInstance = axios.create({
  timeout: REQUEST_TIMEOUT,
  baseURL: PATH_URL
})

axiosInstance.interceptors.request.use((res: InternalAxiosRequestConfig) => {
  ;(res as any)._startTime = Date.now()
  const controller = new AbortController()
  const url = res.url || ''
  res.signal = controller.signal
  abortControllerMap.set(
    import.meta.env.VITE_USE_MOCK === 'true' ? url.replace('/mock', '') : url,
    controller
  )
  return res
})

axiosInstance.interceptors.response.use(
  (res: AxiosResponse) => {
    const startTime = (res.config as any)._startTime

    if (startTime) {
      const duration = Date.now() - startTime
      // 这里的阈值改为 8s
      if (duration > 8000) {
        const userStore = useUserStoreWithOut()
        const userInfo = userStore.getUserInfo as any
        const token = userStore.getToken

        Sentry.captureMessage(`接口响应耗时过长 (${duration}ms): ${res.config.url}`, {
          level: 'warning',
          tags: {
            type: 'slow_api',
            url: res.config.url,
            user_name: userInfo?.username || 'unknown',
            user_id: userInfo?.id || 'unknown'
          },
          extra: {
            duration,
            fullUrl: (res.config.baseURL || '') + (res.config.url || ''),
            btk: token
          }
        })
      }
    }
    const url = res.config.url || ''
    abortControllerMap.delete(url)
    // 这里不能做任何处理，否则后面的 interceptors 拿不到完整的上下文了
    return res
  },
  (error: AxiosError) => {
    console.log('err： ' + error) // for debug

    // 捕获 Axios 错误并添加用户信息到 Sentry
    if (error.config) {
      const userStore = useUserStoreWithOut()
      const userInfo = userStore.getUserInfo as any
      const token = userStore.getToken

      Sentry.withScope((scope) => {
        // 设置用户信息标签，方便搜索
        scope.setTag('user_name', userInfo?.username || 'unknown')
        scope.setTag('user_id', userInfo?.id || 'unknown')
        scope.setTag('api_url', error.config.url)
        
        // 设置 Sentry 的 user 上下文
        scope.setUser({
          id: userInfo?.id || token || 'unknown',
          username: userInfo?.username
        })
        
        // 将 token 作为附加信息
        scope.setExtra('btk', token)
        scope.setExtra('api_config', error.config)

        // 手动上报异常以便携带上述的上下文信息
        Sentry.captureException(error)
      })
    }

    ElMessage.error(error.message)
    return Promise.reject(error)
  }
)

axiosInstance.interceptors.request.use(defaultRequestInterceptors)
axiosInstance.interceptors.response.use(defaultResponseInterceptors)

const service = {
  request: (config: RequestConfig) => {
    return new Promise((resolve, reject) => {
      if (config.interceptors?.requestInterceptors) {
        config = config.interceptors.requestInterceptors(config as any)
      }

      axiosInstance
        .request(config)
        .then((res) => {
          resolve(res)
        })
        .catch((err: any) => {
          reject(err)
        })
    })
  },
  cancelRequest: (url: string | string[]) => {
    const urlList = Array.isArray(url) ? url : [url]
    for (const _url of urlList) {
      abortControllerMap.get(_url)?.abort()
      abortControllerMap.delete(_url)
    }
  },
  cancelAllRequest() {
    for (const [_, controller] of abortControllerMap) {
      controller.abort()
    }
    abortControllerMap.clear()
  }
}

export default service
